import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Skeleton,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  RocketLaunch as BringUpIcon,
  Videocam as CamerasIcon,
  Map as ZonesIcon,
  Policy as PoliciesIcon,
  FactCheck as PreflightIcon,
  CloudUpload as DeploymentsIcon,
  MonitorHeart as ConfirmHealthyIcon,
  MenuBook as DocsIcon,
  ExpandMore as ExpandMoreIcon,
  Build as ToolsIcon,
} from '@mui/icons-material';
import { api, ensureUiScope, persistUiScope } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { getWarehouseCapabilityProfile } from '../utils/warehouseNavigation';
import { devicesApi, type Device } from '../services/devicesApi';
import { metricsApi, type SafetySummary } from '../services/metricsApi';
import { policiesApi, type Policy } from '../services/policiesApi';
import { zonesApi } from '../services/zonesApi';
import {
  PACK_BY_KEY,
  PACK_DISPLAY_ORDER,
  getPackKeyForAlias,
  getPolicySubjectFromEntityTypes,
  inferPolicyAlias,
  readPolicyUiContext,
  type PackKey,
} from '../domain/policies/policyPacks';
import { readNavContext } from '../utils/navContext';
import {
  getStageChipColor,
  getStageLabel,
  pickPrimaryAction,
  type WorkflowAction,
  type WorkflowBlocker,
  type WorkflowStage,
} from '../utils/workflowLanes';
import { buildArtifactFlowPath, REPORT_KIND_LABELS } from '../utils/reportFlow';
import { buildScopedIncidentsPath } from '../utils/incidentsNavigation';
import { sitesApi, type Site } from '../services/sitesApi';
import { SetupRolloutDeviceReadinessAccordion } from '../components/setup/SetupRolloutDeviceReadinessAccordion';
import { SiteLocationSettingsPanel } from '../components/setup/SiteLocationSettingsPanel';
import { getCapabilitiesFromUser, hasCapability } from '../auth/access';
import {
  deviceCountsTowardSetupRolloutGate,
  filterGateDeviceStates,
} from '../utils/setupRolloutScope';
import { LaneSummaryStrip } from '../components/lanes/LaneSummaryStrip';
import { PlatformWorkflowStrip } from '../components/platform/PlatformWorkflowStrip';
import { LaneBlockersPanel } from '../components/lanes/LaneBlockersPanel';
import { LanePrimaryActionCard } from '../components/lanes/LanePrimaryActionCard';
import { LaneStageList } from '../components/lanes/LaneStageList';

type SetupSnapshot = {
  devices: Device[];
  safetySummary: SafetySummary | null;
  zonesCount: number;
  policiesCount: number;
  packCounts: Record<PackKey, number>;
  deploymentsOk: boolean;
  activeDeviceId: string;
  deviceStates: Array<{
    deviceId: string;
    label: string;
    preflightStatus: 'passed' | 'not_ready' | 'unknown';
    rolloutStatus: 'converged' | 'pending' | 'drifted' | 'unknown';
    countsTowardSiteGate: boolean;
  }>;
  errors: string[];
};

const PAGE_TIMEOUT_MS = 8000;

function withTimeout<T>(promise: Promise<T>, label: string, timeoutMs: number = PAGE_TIMEOUT_MS): Promise<T> {
  let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutHandle = setTimeout(() => reject(new Error(`${label} timed out`)), timeoutMs);
  });
  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timeoutHandle) clearTimeout(timeoutHandle);
  }) as Promise<T>;
}

function resolveSetupPackKey(policy: Policy): PackKey {
  const uiContext = readPolicyUiContext(policy);
  const persistedAlias = String(uiContext?.policy_alias || '').trim();
  if (persistedAlias) return getPackKeyForAlias(persistedAlias);
  const inferredAlias = inferPolicyAlias({
    policyType: String(policy.policy_type || ''),
    subject: getPolicySubjectFromEntityTypes(policy.scope?.entity_types),
    zoneIds: policy.scope?.zone_ids,
  });
  return getPackKeyForAlias(inferredAlias);
}

function derivePackCounts(policies: Policy[]): Record<PackKey, number> {
  const counts: Record<PackKey, number> = {
    core_safety: 0,
    forklift_safety: 0,
    worker_protection: 0,
  };
  for (const policy of policies || []) {
    counts[resolveSetupPackKey(policy)] += 1;
  }
  return counts;
}

function readSetupWorkspaceScope(): { orgId: string; siteId: string } {
  const resolved = ensureUiScope();
  return {
    orgId: String(resolved.orgId || '').trim(),
    siteId: String(resolved.siteId || '').trim(),
  };
}

const SetupRolloutHome: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { effectiveUser } = useAuth();
  const capabilityProfile = useMemo(() => getWarehouseCapabilityProfile(effectiveUser), [effectiveUser]);
  const canWriteSites = useMemo(
    () => hasCapability(getCapabilitiesFromUser(effectiveUser), 'sites:write'),
    [effectiveUser],
  );
  const [loading, setLoading] = useState(true);
  const [evidenceLoading, setEvidenceLoading] = useState(false);
  const [snapshot, setSnapshot] = useState<SetupSnapshot>({
    devices: [],
    safetySummary: null,
    zonesCount: 0,
    policiesCount: 0,
    packCounts: { core_safety: 0, forklift_safety: 0, worker_protection: 0 },
    deploymentsOk: true,
    activeDeviceId: '',
    deviceStates: [],
    errors: [],
  });

  const [scope, setScope] = useState(() => readSetupWorkspaceScope());
  const [onboardingPack, setOnboardingPack] = useState<PackKey | null>(null);
  const [onboardingOrgId, setOnboardingOrgId] = useState(String(scope.orgId || '').trim() || 'default');
  const [onboardingSiteId, setOnboardingSiteId] = useState(String(scope.siteId || '').trim() || 'default');
  const [onboardingSites, setOnboardingSites] = useState<Site[]>([]);
  const [onboardingError, setOnboardingError] = useState<string | null>(null);
  const hasScope = Boolean(scope.orgId && scope.siteId);

  useEffect(() => {
    const onScopeChanged = () => setScope(readSetupWorkspaceScope());
    window.addEventListener('edgentik:scope-changed', onScopeChanged);
    return () => window.removeEventListener('edgentik:scope-changed', onScopeChanged);
  }, []);

  useEffect(() => {
    const nextOrg = String(scope.orgId || '').trim() || 'default';
    const nextSite = String(scope.siteId || '').trim() || 'default';
    setOnboardingOrgId(nextOrg);
    setOnboardingSiteId(nextSite);
  }, [scope.orgId, scope.siteId]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requestedPack = String(params.get('pack') || '').trim() as PackKey;
    if (requestedPack && PACK_BY_KEY[requestedPack]) {
      setOnboardingPack(requestedPack);
    }
  }, [location.search]);

  useEffect(() => {
    let cancelled = false;
    const orgId = String(onboardingOrgId || '').trim();
    if (!orgId) {
      setOnboardingSites([]);
      return;
    }
    void sitesApi
      .listSites(orgId)
      .then((nextSites) => {
        if (!cancelled) setOnboardingSites(nextSites);
      })
      .catch(() => {
        if (!cancelled) setOnboardingSites([]);
      });
    return () => {
      cancelled = true;
    };
  }, [onboardingOrgId]);
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!hasScope) {
        setSnapshot((prev) => ({
          ...prev,
          errors: ['Org/site scope is not set yet. Re-login or choose scope from the header.'],
        }));
        setLoading(false);
        return;
      }

      setLoading(true);

      const results = await Promise.allSettled([
        withTimeout(devicesApi.listDevices(), 'Devices'),
        withTimeout(metricsApi.getSafetySummary(), 'Safety summary'),
        withTimeout(zonesApi.listZones(undefined, undefined, undefined, undefined, true, 'warehouse', scope.orgId, scope.siteId), 'Zones'),
        withTimeout(policiesApi.listPolicies(scope.siteId, true, 'warehouse', scope.orgId), 'Policies'),
      ]);

      if (cancelled) return;

      const errors: string[] = [];
      const read = <T,>(result: PromiseSettledResult<T>, label: string): T | null => {
        if (result.status === 'fulfilled') return result.value;
        errors.push(`${label}: ${result.reason?.message || 'unavailable'}`);
        return null;
      };

      const devices = read(results[0], 'Devices') || [];
      const safetySummary = read(results[1], 'Safety summary');
      const zonesResponse = read(results[2], 'Zones');
      const policiesResponse = read(results[3], 'Policies');
      const policies = Array.isArray(policiesResponse?.policies) ? (policiesResponse?.policies || []) : [];
      const ctx = readNavContext();
      const activeDeviceId = devices.some((d) => d.device_id === ctx.device_id)
        ? String(ctx.device_id)
        : String(devices[0]?.device_id || '');

      const gateDevices = devices.filter(deviceCountsTowardSetupRolloutGate);

      const baseDeviceStates = devices.map((device) => ({
        deviceId: device.device_id,
        label: device.name && device.name !== device.device_id ? `${device.name} (${device.device_id})` : device.device_id,
        preflightStatus: 'unknown' as const,
        rolloutStatus: 'unknown' as const,
        countsTowardSiteGate: deviceCountsTowardSetupRolloutGate(device),
      }));

      setSnapshot({
        devices,
        safetySummary,
        zonesCount: zonesResponse?.count || zonesResponse?.zones?.length || 0,
        policiesCount: policiesResponse?.count || policies.length || 0,
        packCounts: derivePackCounts(policies),
        deploymentsOk: true,
        activeDeviceId,
        deviceStates: baseDeviceStates,
        errors,
      });
      setLoading(false);

      if (devices.length === 0) {
        setEvidenceLoading(false);
        return;
      }

      setEvidenceLoading(true);

      const preflightResults = await Promise.allSettled(
        gateDevices.map(async (device) => {
          const pf = await withTimeout(
            api.get(`/api/v1/pipeline/devices/${encodeURIComponent(device.device_id)}/edge/preflight/latest`, { timeout: 6000 }),
            `Preflight ${device.device_id}`,
            6500
          );
          const data = pf?.data;
          return {
            deviceId: device.device_id,
            status: data?.ok === true || data?.exit_code === 0 || data?.preflight_passed === true
              ? 'passed'
              : 'not_ready' as const,
          };
        })
      );

      const preflightStatusByDevice = new Map<string, 'passed' | 'not_ready' | 'unknown'>();
      preflightResults.forEach((result, index) => {
        const deviceId = gateDevices[index]?.device_id;
        if (!deviceId) return;
        if (result.status === 'fulfilled') {
          preflightStatusByDevice.set(deviceId, result.value.status as 'passed' | 'not_ready');
        } else {
          preflightStatusByDevice.set(deviceId, 'unknown');
        }
      });

      let convergenceItems: Array<{ device_id?: string; overall_status?: string }> = [];
      let deploymentsOk = true;
      try {
        const convergence = await withTimeout(
          api.post('/api/v1/deployments/clarity/convergence_batch', {
            org_id: scope.orgId,
            site_id: scope.siteId,
            device_ids: gateDevices.map((device) => device.device_id),
            limit: Math.max(gateDevices.length, 1),
          }),
          'Deployments',
          7000
        );
        convergenceItems = Array.isArray(convergence?.data?.items) ? convergence.data.items : [];
      } catch (error: any) {
        deploymentsOk = false;
        errors.push(`Deployments: ${error?.message || 'unavailable'}`);
      }

      const rolloutStatusByDevice = new Map<string, 'converged' | 'pending' | 'drifted' | 'unknown'>();
      convergenceItems.forEach((item) => {
        const deviceId = String(item?.device_id || '').trim();
        const status = String(item?.overall_status || '').trim().toLowerCase();
        if (!deviceId) return;
        if (status === 'converged' || status === 'pending' || status === 'drifted') {
          rolloutStatusByDevice.set(deviceId, status);
          return;
        }
        rolloutStatusByDevice.set(deviceId, 'unknown');
      });

      const deviceStates = devices.map((device) => ({
        deviceId: device.device_id,
        label: device.name && device.name !== device.device_id ? `${device.name} (${device.device_id})` : device.device_id,
        preflightStatus: deviceCountsTowardSetupRolloutGate(device)
          ? (preflightStatusByDevice.get(device.device_id) || 'unknown')
          : 'unknown',
        rolloutStatus: deviceCountsTowardSetupRolloutGate(device)
          ? (rolloutStatusByDevice.get(device.device_id) || 'unknown')
          : 'unknown',
        countsTowardSiteGate: deviceCountsTowardSetupRolloutGate(device),
      }));

      if (cancelled) return;

      setSnapshot((prev) => ({
        ...prev,
        deploymentsOk,
        activeDeviceId,
        deviceStates,
        errors,
      }));
      setEvidenceLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [hasScope, scope.orgId, scope.siteId]);

  const onboardingSelectedPack = onboardingPack ? PACK_BY_KEY[onboardingPack] : null;
  const onboardingScopeReady = Boolean(String(onboardingOrgId || '').trim() && String(onboardingSiteId || '').trim());
  const onboardingScopeMatchesWorkspace =
    String(onboardingOrgId || '').trim() === String(scope.orgId || '').trim() &&
    String(onboardingSiteId || '').trim() === String(scope.siteId || '').trim();
  const knownOnboardingSites = useMemo(
    () =>
      Array.from(
        new Map(
          onboardingSites
            .filter((site) => String(site.site_id || '').trim())
            .map((site) => [String(site.site_id || '').trim(), site]),
        ).values(),
      ),
    [onboardingSites],
  );

  const applyOnboardingScope = useCallback(() => {
    const orgId = String(onboardingOrgId || '').trim();
    const siteId = String(onboardingSiteId || '').trim();
    if (!orgId || !siteId) {
      setOnboardingError('Choose both org and site scope before continuing.');
      return false;
    }
    if (!persistUiScope(orgId, siteId, { userSet: true })) {
      setOnboardingError('Failed to persist the selected scope.');
      return false;
    }
    setOnboardingError(null);
    setScope({ orgId, siteId });
    window.dispatchEvent(new CustomEvent('edgentik:scope-changed', { detail: { orgId, siteId } }));
    return true;
  }, [onboardingOrgId, onboardingSiteId]);

  const continueWithOnboardingPath = useCallback((path: string) => {
    if (!applyOnboardingScope()) return;
    navigate(path);
  }, [applyOnboardingScope, navigate]);

  const stages = useMemo<WorkflowStage[]>(() => {
    const hasDevices = snapshot.devices.length > 0;
    const gateDeviceStates = filterGateDeviceStates(snapshot.deviceStates);
    const gateDeviceCount = gateDeviceStates.length;
    const hasCameras = (snapshot.safetySummary?.cameras_online || 0) > 0;
    const hasZones = snapshot.zonesCount > 0;
    const hasPolicies = snapshot.policiesCount > 0;
    const preflightPassedCount = gateDeviceStates.filter((device) => device.preflightStatus === 'passed').length;
    const preflightKnownCount = gateDeviceStates.filter((device) => device.preflightStatus !== 'unknown').length;
    const preflightUnknownCount = gateDeviceStates.filter((device) => device.preflightStatus === 'unknown').length;
    const sitePreflightPassed =
      gateDeviceCount > 0 && gateDeviceStates.every((device) => device.preflightStatus === 'passed');
    const convergedCount = gateDeviceStates.filter((device) => device.rolloutStatus === 'converged').length;
    const siteRolloutConverged =
      gateDeviceCount > 0 && gateDeviceStates.every((device) => device.rolloutStatus === 'converged');
    const hasRolloutEvidence = gateDeviceStates.some((device) => device.rolloutStatus !== 'unknown');

    return [
      {
        id: 'bringup',
        label: 'Bring Up Device',
        status: hasDevices ? 'done' : 'next',
        detail: hasDevices
          ? `${snapshot.devices.length} device${snapshot.devices.length === 1 ? '' : 's'} visible in scope.`
          : 'No devices are visible in scope yet. Start commissioning here before staging the setup posture.',
        path: '/bringup',
      },
      {
        id: 'cameras',
        label: 'Verify Cameras',
        status: hasCameras ? 'done' : hasDevices ? 'next' : 'blocked',
        detail: hasCameras
          ? `${snapshot.safetySummary?.cameras_online || 0} camera${(snapshot.safetySummary?.cameras_online || 0) === 1 ? '' : 's'} online.`
          : 'Confirm camera connectivity and assignment before defining operational scope.',
        path: '/cameras',
      },
      {
        id: 'zones',
        label: 'Define Zones',
        status: hasZones ? 'done' : hasCameras ? 'next' : 'blocked',
        detail: hasZones
          ? `${snapshot.zonesCount} zone${snapshot.zonesCount === 1 ? '' : 's'} defined.`
          : 'Zone-dependent behavior should not move forward until layout and restricted areas are defined.',
        path: '/zones',
      },
      {
        id: 'policies',
        label: 'Choose Pack & Policy',
        status: hasPolicies ? 'done' : hasZones ? 'next' : 'blocked',
        detail: hasPolicies
          ? `${snapshot.policiesCount} ${snapshot.policiesCount === 1 ? 'policy is' : 'policies are'} staged for this scope.`
          : 'Start with one pack, then choose the specific policy inside it once the physical layout is represented correctly.',
        path: '/policies',
      },
      {
        id: 'preflight',
        label: 'Validate Preflight',
        status: sitePreflightPassed ? 'done' : hasPolicies ? 'next' : 'blocked',
        detail: evidenceLoading && hasPolicies
          ? 'Checking latest preflight results across devices in this site.'
          : sitePreflightPassed
            ? `All ${gateDeviceCount} in-service device${gateDeviceCount === 1 ? '' : 's'} passed preflight.`
            : gateDeviceCount === 0
              ? 'No in-service devices in this site yet (offline inventory is excluded).'
            : hasPolicies && preflightKnownCount === 0
              ? `Preflight results are currently unavailable for all ${gateDeviceCount} in-service device${gateDeviceCount === 1 ? '' : 's'}.`
              : hasPolicies && preflightUnknownCount > 0
                ? `${preflightPassedCount}/${gateDeviceCount} in-service device${gateDeviceCount === 1 ? '' : 's'} passed preflight; ${preflightUnknownCount} unavailable.`
                : hasPolicies
                  ? `${preflightPassedCount}/${gateDeviceCount} in-service device${gateDeviceCount === 1 ? '' : 's'} passed preflight.`
                  : 'Run a preflight check once devices, cameras, zones, and policies are ready.',
        path: snapshot.activeDeviceId
          ? `/preflight?device=${encodeURIComponent(snapshot.activeDeviceId)}`
          : '/preflight',
      },
      {
        id: 'deploy',
        label: 'Deploy Changes',
        status: !snapshot.deploymentsOk
          ? 'blocked'
          : siteRolloutConverged
            ? 'done'
            : sitePreflightPassed
              ? 'ready'
              : 'blocked',
        detail: evidenceLoading
          ? 'Checking rollout convergence across devices in this site.'
          : !snapshot.deploymentsOk
          ? 'Deployments are unavailable right now (failed to load). Open Deployments to retry or check backend status.'
          : siteRolloutConverged
            ? `All ${gateDeviceCount} in-service device${gateDeviceCount === 1 ? '' : 's'} converged for this site.`
            : hasRolloutEvidence
              ? `${convergedCount}/${gateDeviceCount} in-service device${gateDeviceCount === 1 ? '' : 's'} converged for this site.`
              : 'Deployments become meaningful once every in-service device passes preflight.',
        path: '/deployments',
      },
      {
        id: 'confirm-healthy',
        label: 'Confirm Healthy',
        status: siteRolloutConverged ? 'next' : 'blocked',
        detail: siteRolloutConverged
          ? 'Verify post-deploy health in Diagnostics before handing off to Operations or Acceptance.'
          : 'Complete deployment first, then confirm the commissioned scope is healthy.',
        path: snapshot.activeDeviceId
          ? `/diagnostics?device=${encodeURIComponent(snapshot.activeDeviceId)}`
          : '/diagnostics',
      },
    ];
  }, [snapshot, evidenceLoading]);

  const blockers = useMemo<WorkflowBlocker[]>(() => {
    const items: WorkflowBlocker[] = [];
    if (snapshot.devices.length === 0) {
      items.push({
        id: 'no-devices',
        title: 'No devices in scope',
        detail: 'Bring up or claim a device before trying to configure cameras, zones, pack-driven policies, or deployments.',
        severity: 'error',
        actionLabel: 'Open Bring Up',
        actionPath: '/bringup',
      });
    }
    if (snapshot.devices.length > 0 && (snapshot.safetySummary?.cameras_online || 0) === 0) {
      items.push({
        id: 'no-cameras',
        title: 'Devices exist, but cameras are not online',
        detail: 'Camera verification is the next critical checkpoint before zones or policy behavior will feel trustworthy.',
        severity: 'warning',
        actionLabel: 'Open Cameras',
        actionPath: '/cameras',
      });
    }
    if ((snapshot.safetySummary?.cameras_online || 0) > 0 && snapshot.zonesCount === 0) {
      items.push({
        id: 'no-zones',
        title: 'No zones defined yet',
        detail: 'The site layout is not represented yet, so zone-dependent policies and acceptance work will be misleading.',
        severity: 'warning',
        actionLabel: 'Open Zones',
        actionPath: '/zones',
      });
    }
    if (snapshot.zonesCount > 0 && snapshot.policiesCount === 0) {
      items.push({
        id: 'no-policies',
        title: 'No policies are active for this scope',
        detail: 'The physical layout exists, but the staged pack and policy posture for this scope is not configured yet.',
        severity: 'info',
        actionLabel: 'Open Policies',
        actionPath: '/policies',
      });
    }
    if (snapshot.policiesCount > 0) {
      const gateDeviceStates = filterGateDeviceStates(snapshot.deviceStates);
      const gateDeviceCount = gateDeviceStates.length;
      const preflightPassedCount = gateDeviceStates.filter((device) => device.preflightStatus === 'passed').length;
      const preflightFailedCount = gateDeviceStates.filter((device) => device.preflightStatus === 'not_ready').length;
      const preflightUnknownCount = gateDeviceStates.filter((device) => device.preflightStatus === 'unknown').length;
      if (!evidenceLoading && gateDeviceCount > 0 && preflightFailedCount > 0) {
        items.push({
          id: 'preflight-incomplete',
          title: 'Some in-service devices have not passed preflight yet',
          detail: `${preflightPassedCount} passed, ${preflightFailedCount} not ready${preflightUnknownCount > 0 ? `, ${preflightUnknownCount} unavailable` : ''} (offline inventory excluded).`,
          severity: 'warning',
          actionLabel: 'Open Preflight',
          actionPath: snapshot.activeDeviceId ? `/preflight?device=${encodeURIComponent(snapshot.activeDeviceId)}` : '/preflight',
        });
      }
      const convergedCount = gateDeviceStates.filter((device) => device.rolloutStatus === 'converged').length;
      if (!evidenceLoading && snapshot.deploymentsOk && gateDeviceCount > 0 && convergedCount < gateDeviceCount) {
        items.push({
          id: 'deploy-incomplete',
          title: 'Site rollout is not converged yet',
          detail: `${convergedCount}/${gateDeviceCount} in-service device${gateDeviceCount === 1 ? '' : 's'} converged (offline inventory excluded).`,
          severity: 'info',
          actionLabel: 'Open Deployments',
          actionPath: '/deployments',
        });
      }
    }
    return items;
  }, [snapshot, evidenceLoading]);

  const primaryAction = useMemo<WorkflowAction>(
    () =>
      pickPrimaryAction(stages, {
        title: 'Review staged rollout state',
        detail: 'The core rollout stages are present. Use Deployments or Preflight to verify the current applied state.',
        label: 'Open Deployments',
        path: '/deployments',
        tone: 'primary',
      }),
    [stages]
  );

  const siteRolloutConverged = useMemo(() => {
    const gateDeviceStates = filterGateDeviceStates(snapshot.deviceStates);
    return gateDeviceStates.length > 0 && gateDeviceStates.every((device) => device.rolloutStatus === 'converged');
  }, [snapshot.deviceStates]);

  const activePackEntries = useMemo(
    () => PACK_DISPLAY_ORDER.map((packKey) => ({ packKey, count: snapshot.packCounts[packKey] || 0 })).filter((entry) => entry.count > 0),
    [snapshot.packCounts],
  );

  const pilotPackPosture = useMemo(() => {
    if (snapshot.policiesCount === 0) {
      return {
        severity: 'info' as const,
        title: 'No pack staged yet',
        detail: 'The default Epic 54 motion is still to choose one pack first, then stage the policy inside that pack for this scope.',
      };
    }
    if (activePackEntries.length === 1) {
      return {
        severity: 'success' as const,
        title: 'One-pack pilot motion is intact',
        detail: `${PACK_BY_KEY[activePackEntries[0].packKey].label} is the only staged pack in this scope right now.`,
      };
    }
    return {
      severity: 'warning' as const,
      title: 'Multiple packs are staged in this scope',
      detail: 'That can be valid later, but the default Founding Pilot motion is one pack at a time. Be explicit about why this site needs multi-pack rollout now.',
    };
  }, [activePackEntries, snapshot.policiesCount]);

  const expertToolLinks = useMemo(() => {
    const links: Array<{ label: string; path: string }> = [];
    if (capabilityProfile.canReadDevices) {
      links.push({ label: 'Devices & Gateways', path: '/devices' });
    }
    if (capabilityProfile.hasSetupWriteAccess) {
      links.push({ label: 'Device Control', path: '/devices/control' });
    }
    if (capabilityProfile.canReadCameras) links.push({ label: 'Cameras', path: '/cameras' });
    if (capabilityProfile.hasSetupReadAccess) {
      links.push({ label: 'Zones', path: '/zones' });
    }
    if (capabilityProfile.canReadPolicies) links.push({ label: 'Policies', path: '/policies' });
    if (capabilityProfile.canReadBlueprints) {
      links.push({ label: 'Runtime Blueprints', path: '/blueprints' });
      links.push({ label: 'Control-plane Snapshots', path: '/config-snapshots' });
    }
    if (capabilityProfile.canReadDevices) links.push({ label: 'Agents', path: '/agents' });
    if (capabilityProfile.canReadDeployments) {
      links.push({ label: 'Missions & Settings', path: '/configurations' });
    }
    if (capabilityProfile.hasHealthAccess) links.push({ label: 'Diagnostics', path: '/diagnostics' });
    return links;
  }, [capabilityProfile]);

  const handoffActions = useMemo(() => {
    const actions: Array<{ label: string; path: string }> = [];
    if (capabilityProfile.hasHealthAccess) {
      actions.push({ label: 'Open Diagnostics', path: '/diagnostics' });
    }
    if (capabilityProfile.hasLiveOpsAccess) {
      actions.push({ label: 'Open incident review', path: buildScopedIncidentsPath('/incidents') });
    }
    if (capabilityProfile.hasAcceptanceAccess) {
      actions.push({ label: 'Open proof workspace', path: '/acceptance' });
    }
    return actions;
  }, [capabilityProfile]);

  const readinessActions = useMemo(() => {
    const actions: Array<{ label: string; path: string }> = [];
    if (capabilityProfile.canManageAcceptanceArtifacts && capabilityProfile.canOpenAcceptanceReportFlow) {
      actions.push({
        label: `Start ${REPORT_KIND_LABELS.cra_readiness}`,
        path: buildArtifactFlowPath({
          kind: 'cra_readiness',
          task: 'camera_acceptance',
          orgId: scope.orgId,
          siteId: scope.siteId,
          deviceId: snapshot.activeDeviceId || undefined,
        }),
      });
      actions.push({
        label: `Start ${REPORT_KIND_LABELS.site_readiness}`,
        path: buildArtifactFlowPath({
          kind: 'site_readiness',
          task: 'site_acceptance',
          orgId: scope.orgId,
          siteId: scope.siteId,
        }),
      });
    }
    if (capabilityProfile.hasAcceptanceAccess) {
      actions.push({ label: 'Open proof workspace', path: '/acceptance' });
    }
    return actions;
  }, [
    capabilityProfile.canManageAcceptanceArtifacts,
    capabilityProfile.canOpenAcceptanceReportFlow,
    capabilityProfile.hasAcceptanceAccess,
    scope.orgId,
    scope.siteId,
    snapshot.activeDeviceId,
  ]);

  const setupStateLabel = useMemo(() => {
    if (!hasScope) return 'Scope required';
    if (siteRolloutConverged) return 'Commissioning complete';
    if (snapshot.devices.length === 0 || (snapshot.safetySummary?.cameras_online || 0) === 0 || snapshot.zonesCount === 0 || snapshot.policiesCount === 0) {
      return 'Needs setup';
    }
    const gateDeviceStates = filterGateDeviceStates(snapshot.deviceStates);
    if (gateDeviceStates.length > 0 && gateDeviceStates.every((device) => device.preflightStatus === 'passed')) {
      return snapshot.deploymentsOk ? 'Ready to deploy' : 'Needs review';
    }
    if (blockers.some((item) => item.severity === 'error')) return 'Blocked';
    if (blockers.length > 0) return 'Needs review';
    return 'Commissioning in progress';
  }, [blockers, hasScope, siteRolloutConverged, snapshot.deploymentsOk, snapshot.deviceStates, snapshot.devices.length, snapshot.policiesCount, snapshot.safetySummary?.cameras_online, snapshot.zonesCount]);

  const topBlockers = useMemo(() => {
    const rank: Record<WorkflowBlocker['severity'], number> = {
      error: 0,
      warning: 1,
      info: 2,
    };
    return [...blockers]
      .sort((a, b) => rank[a.severity] - rank[b.severity])
      .slice(0, 3);
  }, [blockers]);

  const summarySignals = useMemo(() => {
    const items: Array<{ label: string; color: 'warning' | 'info' }> = [];
    if (evidenceLoading) items.push({ label: 'Checking preflight and rollout evidence', color: 'info' });
    return items.slice(0, 2);
  }, [evidenceLoading]);

  const nextAction = useMemo<WorkflowAction>(() => {
    if (siteRolloutConverged) {
      const handoff = handoffActions[0];
      return {
        title: 'Commissioning complete',
        detail: 'Deployment is converged. Confirm healthy and continue to the next owned lane.',
        label: handoff?.label || 'Open Diagnostics',
        path: handoff?.path || '/diagnostics',
        tone: 'success',
      };
    }
    return primaryAction;
  }, [handoffActions, primaryAction, siteRolloutConverged]);

  const setupJourneySteps = useMemo(() => {
    const hasDevices = snapshot.devices.length > 0;
    const camerasOnline = Number(snapshot.safetySummary?.cameras_online || 0);
    const hasZones = snapshot.zonesCount > 0;
    const hasPolicies = snapshot.policiesCount > 0;

    return [
      {
        id: 'devices-cameras',
        number: 1,
        label: 'Bring up devices and cameras',
        status: camerasOnline > 0 ? 'done' : 'next',
        detail: camerasOnline > 0
          ? `${camerasOnline} camera${camerasOnline === 1 ? '' : 's'} online for this scope.`
          : hasDevices
            ? 'Devices exist, but cameras still need verification before the rest of setup will feel trustworthy.'
            : 'Start here by commissioning a device, then confirm cameras are online.',
      },
      {
        id: 'zones-lines',
        number: 2,
        label: 'Define zones and lines',
        status: hasZones ? 'done' : camerasOnline > 0 ? 'next' : 'later',
        detail: hasZones
          ? `${snapshot.zonesCount} zone${snapshot.zonesCount === 1 ? '' : 's'} defined for this scope.`
          : 'Map the physical layout before staging behavior so policies match the real site.',
      },
      {
        id: 'policies',
        number: 3,
        label: 'Stage policies',
        status: hasPolicies ? 'done' : hasZones ? 'next' : 'later',
        detail: hasPolicies
          ? `${snapshot.policiesCount} ${snapshot.policiesCount === 1 ? 'policy is' : 'policies are'} staged for this scope.`
          : 'Choose the pack and policy posture after cameras, zones, and lines are represented correctly.',
      },
      {
        id: 'validate-deploy',
        number: 4,
        label: 'Validate and deploy',
        status: siteRolloutConverged ? 'done' : hasPolicies ? 'next' : 'later',
        detail: siteRolloutConverged
          ? 'Preflight and deployment are converged for in-service devices in this scope.'
          : 'Run preflight, then use Deployments to align live edge state (offline inventory does not block this step).',
      },
      {
        id: 'review',
        number: 5,
        label: 'Review incidents',
        status: siteRolloutConverged && capabilityProfile.hasLiveOpsAccess ? 'next' : 'later',
        detail: siteRolloutConverged
          ? 'After deployment is stable, move into incident review to confirm live behavior.'
          : 'Incident review becomes meaningful after the configure lane is deployed and healthy.',
      },
      {
        id: 'prove',
        number: 6,
        label: 'Generate proof and readiness',
        status: siteRolloutConverged && capabilityProfile.hasAcceptanceAccess ? 'next' : 'later',
        detail: siteRolloutConverged
          ? 'Use Acceptance to turn resolved operational work into evidence, readiness review, and handoff artifacts.'
          : 'Proof and readiness come after the site is configured, validated, and behaving correctly in review.',
      },
    ] as const;
  }, [
    capabilityProfile.hasAcceptanceAccess,
    capabilityProfile.hasLiveOpsAccess,
    siteRolloutConverged,
    snapshot.devices.length,
    snapshot.policiesCount,
    snapshot.safetySummary?.cameras_online,
    snapshot.zonesCount,
  ]);

  const prerequisiteSteps = useMemo(() => {
    const preflightStage = stages.find((stage) => stage.id === 'preflight');
    return [
      {
        id: 'pack',
        label: 'Choose pack',
        state: onboardingSelectedPack ? 'Done' : 'Next',
        color: onboardingSelectedPack ? 'success' : 'warning',
      },
      {
        id: 'scope',
        label: 'Apply scope',
        state: onboardingScopeMatchesWorkspace ? 'Done' : onboardingSelectedPack ? 'Next' : 'Blocked',
        color: onboardingScopeMatchesWorkspace ? 'success' : onboardingSelectedPack ? 'warning' : 'default',
      },
      {
        id: 'preflight',
        label: 'Validate preflight',
        state: preflightStage ? getStageLabel(preflightStage.status) : 'Blocked',
        color: preflightStage ? getStageChipColor(preflightStage.status) : 'default',
      },
      {
        id: 'continue',
        label: 'Continue commissioning',
        state: nextAction.tone === 'success' ? 'Done' : getStageLabel(stages.find((stage) => stage.id === 'deploy')?.status || 'blocked'),
        color: nextAction.tone === 'success' ? 'success' : 'default',
      },
    ] as const;
  }, [nextAction.tone, onboardingScopeMatchesWorkspace, onboardingSelectedPack, stages]);

  const secondaryHandoffActions = useMemo(
    () => (siteRolloutConverged ? handoffActions.slice(1) : []),
    [handoffActions, siteRolloutConverged],
  );

  const renderStageIcon = useCallback((stage: WorkflowStage) => {
    if (stage.id === 'bringup') return <BringUpIcon fontSize="small" />;
    if (stage.id === 'cameras') return <CamerasIcon fontSize="small" />;
    if (stage.id === 'zones') return <ZonesIcon fontSize="small" />;
    if (stage.id === 'policies') return <PoliciesIcon fontSize="small" />;
    if (stage.id === 'preflight') return <PreflightIcon fontSize="small" />;
    if (stage.id === 'confirm-healthy') return <ConfirmHealthyIcon fontSize="small" />;
    return <DeploymentsIcon fontSize="small" />;
  }, []);

  return (
    <Stack spacing={3} sx={{ width: '100%', maxWidth: 1120, pb: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
        <Box sx={{ maxWidth: 760 }}>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.12em' }}>
            Commissioning lane
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            {loading ? <Skeleton variant="text" width={200} /> : 'Setup'}
          </Typography>
          <Typography color="text.secondary" variant="body2">
            Follow the path from bring-up to live review and proof for this site.
          </Typography>
        </Box>
        <Tooltip title="Setup options (docs)">
          <IconButton
            size="small"
            component="a"
            href="/docs/user-guide/setup-options/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <DocsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <PlatformWorkflowStrip currentStep="configure" compact />

      <LaneSummaryStrip
        items={[
          { label: 'Scope', value: `${scope.orgId || '—'} / ${scope.siteId || '—'}` },
          { label: 'Pack', value: onboardingSelectedPack ? onboardingSelectedPack.label : 'Not selected' },
          { label: 'Status', value: setupStateLabel },
        ]}
        signals={[
          ...summarySignals,
          ...(onboardingError ? [{ label: onboardingError, color: 'warning' as const }] : []),
        ]}
      />

      <LaneBlockersPanel
        blockers={topBlockers}
        warningText={snapshot.errors.length > 0 ? `Some setup inputs are unavailable right now: ${snapshot.errors.join(' | ')}` : null}
        subtitle="Resolve the active blockers before continuing commissioning."
        onNavigate={(path) => navigate(path)}
      />

      <LanePrimaryActionCard
        title={nextAction.title}
        detail={nextAction.detail}
        ctaLabel={nextAction.label}
        ctaPath={nextAction.path}
        tone={nextAction.tone}
        onNavigate={(path) => navigate(path)}
        statusLine={
          siteRolloutConverged
            ? 'Setup is complete. Continue into diagnostics, operations, or acceptance.'
            : undefined
        }
        secondaryActions={secondaryHandoffActions}
      />

      <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 2 }}>
        <Stack spacing={1.5}>
          <Box>
            <Typography sx={{ fontWeight: 700 }}>Visible setup order</Typography>
            <Typography variant="body2" color="text.secondary">
              Configure the site in this order, then hand off to Review and the Proof Package once the live edge state is stable.
            </Typography>
          </Box>
          <Stack spacing={1}>
            {setupJourneySteps.map((step) => (
              <Box
                key={step.id}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 2,
                  flexWrap: 'wrap',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  px: 1.5,
                  py: 1.1,
                }}
              >
                <Box sx={{ display: 'flex', gap: 1.25, minWidth: 0, flex: 1 }}>
                  <Chip size="small" color={step.status === 'done' ? 'success' : step.status === 'next' ? 'warning' : 'default'} label={step.number} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {step.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {step.detail}
                    </Typography>
                  </Box>
                </Box>
                <Chip
                  size="small"
                  color={step.status === 'done' ? 'success' : step.status === 'next' ? 'warning' : 'default'}
                  variant={step.status === 'done' ? 'outlined' : 'filled'}
                  label={step.status === 'done' ? 'Done' : step.status === 'next' ? 'Next' : 'Later'}
                />
              </Box>
            ))}
          </Stack>
        </Stack>
      </Paper>

      <Accordion defaultExpanded={!onboardingSelectedPack || !onboardingScopeMatchesWorkspace}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ width: '100%' }}>
            <Typography sx={{ fontWeight: 700 }}>Pack and scope prerequisites</Typography>
            <Typography variant="caption" color="text.secondary">
              Choose pack, apply scope, then continue into preflight and commissioning.
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={1.75}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {prerequisiteSteps.map((step) => (
                <Chip
                  key={step.id}
                  size="small"
                  color={step.color as any}
                  variant={step.state === 'Done' ? 'outlined' : 'filled'}
                  label={`${step.label}: ${step.state}`}
                />
              ))}
            </Stack>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'minmax(160px, 1fr) minmax(160px, 1fr) auto' },
                gap: 1.25,
                alignItems: 'center',
              }}
            >
              <TextField
                size="small"
                label="Org scope"
                value={onboardingOrgId}
                onChange={(event) => {
                  setOnboardingOrgId(event.target.value);
                  setOnboardingError(null);
                }}
                fullWidth
              />
              <TextField
                size="small"
                label="Site scope"
                value={onboardingSiteId}
                onChange={(event) => {
                  setOnboardingSiteId(event.target.value);
                  setOnboardingError(null);
                }}
                fullWidth
              />
              <Button
                variant={onboardingScopeMatchesWorkspace ? 'outlined' : 'contained'}
                onClick={() => {
                  applyOnboardingScope();
                }}
                disabled={!onboardingScopeReady}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {onboardingScopeMatchesWorkspace ? 'Scope applied' : 'Apply scope'}
              </Button>
            </Box>

            {knownOnboardingSites.length > 0 ? (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {knownOnboardingSites.map((site) => {
                  const siteId = String(site.site_id || '').trim();
                  return (
                    <Button
                      key={`${site.org_id}-${siteId}`}
                      size="small"
                      variant={siteId === onboardingSiteId ? 'contained' : 'outlined'}
                      onClick={() => {
                        setOnboardingSiteId(siteId);
                        setOnboardingError(null);
                      }}
                    >
                      {site.display_name || siteId}
                    </Button>
                  );
                })}
              </Stack>
            ) : null}

            <Grid container spacing={1.25}>
              {PACK_DISPLAY_ORDER.map((packKey) => {
                const pack = PACK_BY_KEY[packKey];
                const count = snapshot.packCounts[packKey] || 0;
                const selected = onboardingPack === pack.key;
                return (
                  <Grid item xs={12} md={4} key={pack.key}>
                    <Box sx={{ border: '1px solid', borderColor: selected ? 'primary.main' : 'divider', borderRadius: 1.5, p: 1.25, height: '100%' }}>
                      <Stack spacing={0.75}>
                        <Box display="flex" justifyContent="space-between" alignItems="start" gap={1}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            {pack.label}
                          </Typography>
                          <Chip
                            size="small"
                            color={selected ? 'primary' : count > 0 ? 'info' : 'default'}
                            variant={selected ? 'filled' : 'outlined'}
                            label={selected ? 'Selected' : `${count} staged`}
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {pack.bestFor}
                        </Typography>
                        <Box display="flex" gap={1} flexWrap="wrap">
                          <Button
                            size="small"
                            variant={selected ? 'contained' : 'outlined'}
                            onClick={() => {
                              setOnboardingPack(pack.key);
                              setOnboardingError(null);
                            }}
                          >
                            {selected ? 'Selected' : 'Choose'}
                          </Button>
                          {selected ? (
                            <Button
                              size="small"
                              variant="text"
                              onClick={() =>
                                continueWithOnboardingPath(`/policies?create=1&pack=${encodeURIComponent(pack.key)}`)
                              }
                              disabled={!onboardingScopeReady}
                            >
                              Stage selected pack
                            </Button>
                          ) : null}
                        </Box>
                      </Stack>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Stack>
        </AccordionDetails>
      </Accordion>

      <LaneStageList
        stages={stages}
        onNavigate={(path) => navigate(path)}
        renderStageIcon={renderStageIcon}
      />

      <SetupRolloutDeviceReadinessAccordion
        deviceStates={snapshot.deviceStates}
        evidenceLoading={evidenceLoading}
        onInspectDevice={(deviceId) => navigate(`/diagnostics?device=${encodeURIComponent(deviceId)}`)}
      />

      <SiteLocationSettingsPanel
        orgId={scope.orgId}
        siteId={scope.siteId}
        canWrite={canWriteSites}
      />

      <LaneSummaryStrip
        items={PACK_DISPLAY_ORDER.map((packKey) => ({
          label: PACK_BY_KEY[packKey].label,
          value: `${snapshot.packCounts[packKey] || 0} staged`,
        }))}
        signals={[]}
      >
        <Stack spacing={1.25}>
          <Alert severity={pilotPackPosture.severity}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {pilotPackPosture.title}
            </Typography>
            {pilotPackPosture.detail}
          </Alert>
          <Box display="flex" justifyContent="flex-start">
            <Button
              size="small"
              variant="outlined"
              onClick={() =>
                navigate(
                  onboardingSelectedPack
                    ? `/policies?pack=${encodeURIComponent(onboardingSelectedPack.key)}`
                    : '/policies'
                )
              }
            >
              Review staged policies
            </Button>
          </Box>
        </Stack>
      </LaneSummaryStrip>

      {(readinessActions.length > 0 || capabilityProfile.canOpenAcceptanceReportFlow || siteRolloutConverged) && (
        <Accordion defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box>
              <Typography sx={{ fontWeight: 700 }}>Readiness initiation</Typography>
              <Typography variant="caption" color="text.secondary">
                Downstream proof and readiness prep after the core commissioning lane is stable.
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1.5}>
              {siteRolloutConverged && (
                <Alert
                  severity="success"
                  action={
                    handoffActions.length > 0 ? (
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {handoffActions.map((item) => (
                          <Button
                            key={item.path}
                            color="inherit"
                            size="small"
                            variant="outlined"
                            onClick={() => navigate(item.path)}
                          >
                            {item.label}
                          </Button>
                        ))}
                      </Stack>
                    ) : undefined
                  }
                >
                  Site rollout converged. Confirm healthy, then continue to readiness or handoff.
                </Alert>
              )}
              {capabilityProfile.canOpenAcceptanceReportFlow && !capabilityProfile.canManageAcceptanceArtifacts && (
                <Alert severity="info">
                  Review access is available, but starting the camera readiness report or site readiness review from this scope requires rollout-capable artifact permissions.
                </Alert>
              )}
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {readinessActions.map((item) => (
                  <Button key={item.path} variant={item.path === '/acceptance' ? 'outlined' : 'contained'} onClick={() => navigate(item.path)}>
                    {item.label}
                  </Button>
                ))}
              </Stack>
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}

      {expertToolLinks.length > 0 && (
        <Accordion defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <ToolsIcon sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} />
            <Box>
              <Typography sx={{ fontWeight: 700 }}>Expert configuration tools</Typography>
              <Typography variant="caption" color="text.secondary">
                Open when the lane path is not enough.
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {expertToolLinks.map((item) => (
                <Button key={item.path + item.label} size="small" variant="outlined" onClick={() => navigate(item.path)}>
                  {item.label}
                </Button>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
    </Stack>
  );
};

export default SetupRolloutHome;
